import { BandSettingsSlugs } from "Bands/types";
import { IInfoBlockItem } from "shared/types";

const API_PREFIX = "/api";

const getInfoBlockItemApiName = (type: IInfoBlockItem) => {
  switch (type) {
    default:
    case IInfoBlockItem.contact: {
      return "contacts";
    }
    case IInfoBlockItem.place: {
      return "places";
    }
    case IInfoBlockItem.file: {
      return "files";
    }
    case IInfoBlockItem.image: {
      return "images";
    }
  }
};

const VENUE_API_ENDPOINTS = {
  VENUES: `${API_PREFIX}/venues`,
  SEARCH_VENUES: `${API_PREFIX}/venues/search`,
  VENUE: (id: string) => `${API_PREFIX}/venues/${id}`,
  STAGE: (id: string) => `${API_PREFIX}/stages/${id}`,
  STAGES: `${API_PREFIX}/stages`,
  STAGE_CAPACITIES: `${API_PREFIX}/stage_capacities`,
  STAGE_CAPACITY: (id: string) => `${API_PREFIX}/stage_capacities/${id}`,
  EQUIPMENT_LIST: `${API_PREFIX}/equipment`,
  EQUIPMENT: (id: string) => `${API_PREFIX}/equipment/${id}`,
  VENUE_INFO_BLOCKS: `${API_PREFIX}/venue_info_blocks`,
  VENUE_INFO_BLOCK: (id: string) => `${API_PREFIX}/venue_info_blocks/${id}`,
  STAGE_INFO_BLOCKS: `${API_PREFIX}/stage_info_blocks`,
  STAGE_INFO_BLOCK: (id: string) => `${API_PREFIX}/stage_info_blocks/${id}`,
  PUBLISH_VENUE: (id: string) => `${API_PREFIX}/venues/${id}/publish`,
  UNPUBLISH_VENUE: (id: string) => `${API_PREFIX}/venues/${id}/unpublish`,
  REQUEST_PUBLISH_VENUE: (id: string) =>
    `${API_PREFIX}/venues/${id}/request_publish`,
  VENUE_INVITES: `${API_PREFIX}/venue_invites`,
  VENUE_INVITE: (id: string) => `${API_PREFIX}/venue_invites/${id}`,
  VENUE_INVITE_BATCH: `${API_PREFIX}/venue_invites/batch`,
  SUGGEST_EDIT: (id: string) => `${API_PREFIX}/venues/${id}/suggest_edit`,
};

const UTILITY_API_ENDPOINTS = {
  GEOCODING: `${API_PREFIX}/geocoding`,
  UPLOAD: `${API_PREFIX}/upload`,
  SCHEDULED_FLIGHTS: `${API_PREFIX}/scheduled_flights`,
  FILE: (id: string) => `${API_PREFIX}/files/${id}`,
  INFO_BLOCK_ITEMS: (type: IInfoBlockItem) =>
    `${API_PREFIX}/${getInfoBlockItemApiName(type)}`,
  INFO_BLOCK_ITEM: (id: string, type: IInfoBlockItem) =>
    `${API_PREFIX}/${getInfoBlockItemApiName(type)}/${id}`,
  EVENT_DETAILS_EXPORT: `${API_PREFIX}/event_details_exports`,
  EVENT_DETAILS_EXPORT_CONFIG: (id: string) =>
    `${API_PREFIX}/event_details_exports/${id}`,
};

const USER_API_ENDPOINTS = {
  USER: (id: string) => `${API_PREFIX}/users/${id}`,
  USERS: `${API_PREFIX}/users`,
  USER_ADDRESS: (id: string) => `${API_PREFIX}/user_addresses/${id}`,
  USER_ADDRESSES: `${API_PREFIX}/user_addresses`,
  USER_PHONE_NUMBER: (id: string) => `${API_PREFIX}/user_phone_numbers/${id}`,
  USER_PHONE_NUMBERS: `${API_PREFIX}/user_phone_numbers`,
  USER_EMERGENCY_CONTACT: (id: string) =>
    `${API_PREFIX}/user_emergency_contacts/${id}`,
  USER_EMERGENCY_CONTACTS: `${API_PREFIX}/user_emergency_contacts`,
  USER_HOME_BASE: (id: string) => `${API_PREFIX}/user_home_bases/${id}`,
  USER_HOME_BASES: `${API_PREFIX}/user_home_bases`,
  USER_PASSPORT: (id: string) => `${API_PREFIX}/user_passports/${id}`,
  USER_PASSPORTS: `${API_PREFIX}/user_passports`,
  USER_TRAVEL_VISA: (id: string) => `${API_PREFIX}/user_travel_visas/${id}`,
  USER_TRAVEL_VISAS: `${API_PREFIX}/user_travel_visas`,
  USER_SOCIAL_SECURITY_FORM: (id: string) =>
    `${API_PREFIX}/user_social_security_forms/${id}`,
  USER_SOCIAL_SECURITY_FORMS: `${API_PREFIX}/user_social_security_forms`,
  USER_DRIVING_PERMIT: (id: string) =>
    `${API_PREFIX}/user_driving_permits/${id}`,
  USER_DRIVING_PERMITS: `${API_PREFIX}/user_driving_permits`,
  USER_VEHICLE: (id: string) => `${API_PREFIX}/user_vehicles/${id}`,
  USER_VEHICLES: `${API_PREFIX}/user_vehicles`,
  USER_LOYALTY_PROGRAM: (id: string) =>
    `${API_PREFIX}/user_loyalty_programs/${id}`,
  USER_LOYALTY_PROGRAMS: `${API_PREFIX}/user_loyalty_programs`,
  CHANGE_EMAIL: `${API_PREFIX}/users/change_email`,
  CONFIRM_EMAIL: `${API_PREFIX}/users/email_confirm`,
  USER_API_KEY: `${API_PREFIX}/user_api_keys`,
  USER_API_KEY_DELETE: (id: string) => `${API_PREFIX}/user_api_keys/${id}`,
};

const BAND_API_ENDPOINTS = {
  BAND: (id: string) => `${API_PREFIX}/bands/${id}`,
  BANDS: `${API_PREFIX}/bands`,
  BAND_USERS: `${API_PREFIX}/band_users`,
  BAND_USERS_ME: `${API_PREFIX}/band_users/me`,
  BAND_USER: (id: string) => `${API_PREFIX}/band_users/${id}`,
  ADD_BAND_SETTING: (type: BandSettingsSlugs) => `${API_PREFIX}/${type}`,
  EVENT_SETTING_ITEM: (type: BandSettingsSlugs, id: string) =>
    `${API_PREFIX}/${type}/${id}`,
  EVENTS: `${API_PREFIX}/events`,
  EVENT: (id: string) => `${API_PREFIX}/events/${id}`,
  BAND_INVITATIONS: `${API_PREFIX}/band_invitations`,
  BAND_INVITATION: (id: string) => `${API_PREFIX}/band_invitations/${id}`,
  SEND_INVITATION: (id: string) => `${API_PREFIX}/band_invitations/${id}/send`,
  ACCEPT_INVITATION: (id: string) =>
    `${API_PREFIX}/band_invitations/${id}/accept`,
  REVOKE_INVITATION: (id: string) =>
    `${API_PREFIX}/band_invitations/${id}/revoke`,
  PERMISSION: (id: string) => `${API_PREFIX}/permissions/${id}`,
  PERMISSION_TYPES: `${API_PREFIX}/permission_types`,
  PERMISSION_TYPE: (id: string) => `${API_PREFIX}/permission_types/${id}`,
  PERMISSION_GROUP: (id: string) => `${API_PREFIX}/permission_groups/${id}`,
  BAND_USER_PERMISSIONS: `${API_PREFIX}/band_user_permissions`,
  USER_PERMISSIONS: (id: string) =>
    `${API_PREFIX}/band_users/${id}/permissions`,
  EVENT_PEOPLE_BLOCKS: `${API_PREFIX}/event_people_blocks`,
  EVENT_PEOPLE_BLOCK: (id: string) => `${API_PREFIX}/event_people_blocks/${id}`,
  EVENT_PEOPLE_BLOCK_ADD: (id: string) =>
    `${API_PREFIX}/event_people_blocks/${id}/add_members`,
  EVENT_PEOPLE_BLOCK_USER: (id: string) =>
    `${API_PREFIX}/event_people_block_users/${id}`,
  EVENT_RSVP: (id: string) =>
    `${API_PREFIX}/event_people_block_user_rsvps/${id}`,
  ANSWER_EVENT_RSVP: (id: string) =>
    `${API_PREFIX}/event_people_block_user_rsvps/${id}/answer`,
  EVENT_SCHEDULE_BLOCKS: `${API_PREFIX}/event_schedule_blocks`,
  EVENT_SCHEDULE_BLOCK: (id: string) =>
    `${API_PREFIX}/event_schedule_blocks/${id}`,
  EVENT_SCHEDULE_BLOCK_ENTRIES: `${API_PREFIX}/event_schedule_timesheet_entries`,
  EVENT_SCHEDULE_BLOCK_ENTRY: (id: string) =>
    `${API_PREFIX}/event_schedule_timesheet_entries/${id}`,
  EVENT_INFO_BLOCK_TYPES: `${API_PREFIX}/event_info_block_types`,
  EVENT_INFO_BLOCK_TYPE: (id: string) =>
    `${API_PREFIX}/event_info_block_types/${id}`,
  EVENT_INFO_BLOCKS: `${API_PREFIX}/event_info_blocks`,
  EVENT_INFO_BLOCK: (id: string) => `${API_PREFIX}/event_info_blocks/${id}`,
  EVENT_VENUE_BLOCKS: `${API_PREFIX}/event_venue_blocks`,
  EVENT_VENUE_BLOCK: (id: string) => `${API_PREFIX}/event_venue_blocks/${id}`,
  EVENT_GUESTLIST_BLOCKS: `${API_PREFIX}/event_guest_list_blocks`,
  EVENT_GUESTLIST_BLOCK: (id: string) =>
    `${API_PREFIX}/event_guest_list_blocks/${id}`,
  EVENT_GUESTLIST_BLOCK_API: (id: string) =>
    `${API_PREFIX}/event_guestlist_blocks/${id}`,
  DOWNLOAD_EVENT_GUESTLIST_BLOCK: (id: string) =>
    `${API_PREFIX}/event_guest_list_blocks/${id}/download`,
  EVENT_TICKET_PASSES: `${API_PREFIX}/event_guest_list_ticket_and_pass_types`,
  EVENT_TICKET_PASS: (id: string) =>
    `${API_PREFIX}/event_guest_list_ticket_and_pass_types/${id}`,
  EVENT_GUESTS: `${API_PREFIX}/event_guest_list_block_entries`,
  EVENT_GUEST: (id: string) =>
    `${API_PREFIX}/event_guest_list_block_entries/${id}`,
  EVENT_TRAVEL_BLOCKS: `${API_PREFIX}/event_travel_blocks`,
  EVENT_TRAVEL_BLOCK: (id: string) => `${API_PREFIX}/event_travel_blocks/${id}`,
  EVENT_FLIGHT_TRAVEL_LEGS: `${API_PREFIX}/event_flight_travel_legs`,
  EVENT_FLIGHT_TRAVEL_LEG: (id: string) =>
    `${API_PREFIX}/event_flight_travel_legs/${id}`,
  EVENT_BUS_TRAVEL_LEGS: `${API_PREFIX}/event_bus_travel_legs`,
  EVENT_BUS_TRAVEL_LEG: (id: string) =>
    `${API_PREFIX}/event_bus_travel_legs/${id}`,
  EVENT_TRAIN_TRAVEL_LEGS: `${API_PREFIX}/event_train_travel_legs`,
  EVENT_TRAIN_TRAVEL_LEG: (id: string) =>
    `${API_PREFIX}/event_train_travel_legs/${id}`,
  EVENT_OTHER_TRAVEL_LEGS: `${API_PREFIX}/event_other_travel_legs`,
  EVENT_OTHER_TRAVEL_LEG: (id: string) =>
    `${API_PREFIX}/event_other_travel_legs/${id}`,
  EVENT_SUGGESTED_LOCATIONS: `${API_PREFIX}/event_suggested_locations`,
  EVENT_SUGGESTED_LOCATION: (id: string) =>
    `${API_PREFIX}/event_suggested_locations/${id}`,
  EVENT_TRAVEL_BLOCK_PASSENGERS: `${API_PREFIX}/event_travel_block_passengers`,
  EVENT_TRAVEL_BLOCK_PASSENGER: (id: string) =>
    `${API_PREFIX}/event_travel_block_passengers/${id}`,
  ICALS: `${API_PREFIX}/icals`,
  ICAL: (id: string) => `${API_PREFIX}/icals/${id}`,
  EVENT_ACCOMMODATION_BLOCKS: `${API_PREFIX}/event_accommodation_blocks`,
  EVENT_ACCOMMODATION_BLOCK: (id: string) =>
    `${API_PREFIX}/event_accommodation_blocks/${id}`,
  EVENT_ACCOMMODATION_ROOM_TYPES: `${API_PREFIX}/event_accommodation_room_types`,
  EVENT_ACCOMMODATION_ROOM_TYPES_FOR_BLOCK: (id: string) =>
    `${API_PREFIX}/event_accommodation_room_types?accommodation=/api/event_accommodation_blocks/${id}`, //TODO: Correct way of passing ID.
  EVENT_ACCOMMODATION_ROOM_TYPE: (id: string) =>
    `${API_PREFIX}/event_accommodation_room_types/${id}`,

  EVENT_ACCOMMODATION_RESERVATIONS: `${API_PREFIX}/event_accommodation_reservations`,
  EVENT_ACCOMMODATION_RESERVATION: (id: string) =>
    `${API_PREFIX}/event_accommodation_reservations/${id}`,
  EVENT_ACCOMMODATION_ROOMS: `${API_PREFIX}/event_accommodation_rooms`,
  EVENT_ACCOMMODATION_ROOM: (id: string) =>
    `${API_PREFIX}/event_accommodation_rooms/${id}`,

  EVENT_ACCOMMODATION_RESERVATIONS_ADD_PEOPLE: `${API_PREFIX}/event_accommodation_reservations/add_people`,
  IMPORT_EVENT_BLOCKS: (targetId: string) => `${targetId}/import`,
  EVENTS_EXPORT_CONFIGS: `${API_PREFIX}/events_export_configs`,
  EVENTS_EXPORTS: `${API_PREFIX}/events_exports`,
  EVENT_TYPES: (targetId: string) => `${API_PREFIX}/event_types/${targetId}`,
};

const COMPANY_API_ENDPOINTS = {
  COMPANIES: `${API_PREFIX}/companies`,
  COMPANY: (id: string) => `${API_PREFIX}/companies/${id}`,
  COMPANY_PAYMENT_METHODS: (id: string) =>
    `${API_PREFIX}/companies/${id}/payment_methods`,
  SUBSCRIPTIONS: `${API_PREFIX}/subscriptions`,
  SUBSCRIPTION: (id: string) => `${API_PREFIX}/subscriptions/${id}`,
  CUSTOM_SUBSCRIPTIONS: `${API_PREFIX}/custom_subscriptions`,
  CUSTOM_SUBSCRIPTION: (id: string) =>
    `${API_PREFIX}/custom_subscriptions/${id}`,
  CUSTOM_SUBSCRIPTION_REQUEST: (id: string) =>
    `${API_PREFIX}/custom_subscription_requests/${id}`,
  CUSTOM_SUBSCRIPTION_REQUESTS: `${API_PREFIX}/custom_subscription_requests`,
  CHANGE_PAYMENT_METHOD: (id: string) =>
    `${API_PREFIX}/subscriptions/${id}/change_payment_method`,
  EDIT_SUBSCRIPTION: (id: string) =>
    `${API_PREFIX}/subscriptions/${id}/change_plan`,
  CANCEL_SUBSCRIPTION: (id: string) =>
    `${API_PREFIX}/subscriptions/${id}/cancel`,
  COMPANY_INVOICES: `${API_PREFIX}/invoices`,
  COMPANY_USER: (id: string) => `${API_PREFIX}/company_users/${id}`,
  INVOICE: (id: string) => `${API_PREFIX}/invoices/${id}`,
  START_TRIAL: `${API_PREFIX}/trial_subscriptions`,
  EDIT_TRIAL: (id: string) => `${API_PREFIX}/trial_subscriptions/${id}`,
};

export const API_ENDPOINTS = {
  ...UTILITY_API_ENDPOINTS,
  ...USER_API_ENDPOINTS,
  ...VENUE_API_ENDPOINTS,
  ...BAND_API_ENDPOINTS,
  ...COMPANY_API_ENDPOINTS,
};

export const API_CONTENT_TYPE = {
  JSON: "application/json",
  PATCH: "application/merge-patch+json",
};

export const API_ACCEPT = {
  JSON_API: "application/vnd.api+json",
};
